import { HoldingSession, HoldingSessions, OmniaEvent } from "../../api/types"
import { labels } from "../../generated/labels"
import { HoldingUser } from "../../holding"
import { HoldingUserRole } from "../../permissions/holding/api"
import { RoleId } from "../../user"
import { createMap } from "../../utils/array"
import { EventText } from "./eventText"

/** @internal */
export interface SessionVm {
    userId: number
    email: string
    userName: string
    current: boolean
    admin: boolean
    readOnly: boolean
    holdingRole: string
    startDate?: Date
    module?: string
    activity?: string
    clientState?: HoldingSession["ClientState"]
}

/** @internal */
export function createSessionsVm(sessions: HoldingSessions, users: HoldingUser[], activityTextProvider: (event: OmniaEvent) => EventText): SessionVm[] {

    const { active } = sessions

    const usersMap = createMap(users, u => u.UserId)

    const online: SessionVm[] = active.map(a => ({
        userId: a.UserId,
        email: a.UserEmail,
        userName: a.UserName,
        admin: a.Role <= RoleId.Admin,
        holdingRole: getHoldingRoleText(usersMap.get(a.UserId)?.HoldingRole),
        readOnly: a.ReadOnly,
        current: a.CurrentSession,
        startDate: a.StartDate,
        module: getSessionModuleName(a),
        activity: getSessionActivity(a, activityTextProvider),
        clientState: a.ClientState
    }))

    const offline: SessionVm[] = users
        .filter(u => active.every(s => s.UserId != u.UserId))
        .map(u => ({
            userId: u.UserId,
            email: u.Email,
            userName: [u.FirstName, u.LastName].filter(n => !!n).join(" "),
            admin: u.Role <= RoleId.Admin,
            holdingRole: getHoldingRoleText(u.HoldingRole),
            readOnly: false,
            current: false,
        }))

    const start = (a: SessionVm) => a.startDate ? a.startDate.valueOf() : Number.MAX_SAFE_INTEGER

    const all = online.concat(offline)

    const currentUser = all.find(s => s.current ? s.userId : undefined)?.userId

    const sorted = all.sort((a, b) =>
        // Current session first
        ((a.current ? 0 : 1) - (b.current ? 0 : 1))
        // Other sessions from the same user
        || ((a.userId == currentUser ? 0 : 1) - (b.userId == currentUser ? 0 : 1))
        // Admins last
        || ((a.admin ? 1 : 0) - (b.admin ? 1 : 0))
        // Then by ascending start date
        || (start(a) - start(b))
    )

    return sorted

}

function getSessionModuleName(s: HoldingSession) {
    const id = s.ClientState?.moduleId
    return id ? labels.OmniaModuleId(id) : undefined
}


function getSessionActivity(s: HoldingSession, activityTextProvider: (event: OmniaEvent) => EventText) {

    const a = s.ClientState?.activity

    if (a) {

        const event: OmniaEvent = {
            Action: a.action,
            Data: a.data,
            ObjectType: a.resourceType,
            // TODO: source/module
            Source: undefined
        }

        return activityTextProvider(event).text

    }

}


const roles = {
    [HoldingUserRole.Member]: "Member",
    [HoldingUserRole.Owner]: "Owner"
}

function getHoldingRoleText(role: HoldingUserRole) {
    return role
        ? roles[role]
        : undefined
}
